import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AlertCircle } from '@lifeomic/chromicons';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCRDTemplates } from '../../../../AITemplates/hooks/useCompositeReportDefinitionTemplates';
import { useTemplatingItemDetailQuery } from '../../../../AITemplates/hooks/useTemplatingItem';
import { CompositeReportDefinitionDetail } from '../../../../AITemplates/models';
import { KnownImpressionTypes } from '../../AITools/ImpressionEditor/ImpressionItem';
import { EXECUTIVE_SUMMARY_IMPRESSION_TYPES } from '../constants';
import { ExecutiveSummaryData } from '../executive-summary';
import { messages } from '../messages';
import { useRecursiveCRDFetch } from '../useRecursiveCRDFetch';
import { isAiGenerated } from '../utils';
import { CRDSelect } from './CRDSelect';
import { GenerateExecutiveSummary } from './GenerateExecutiveSummary';
import { ImpressionTypeSelect } from './ImpressionTypeSelect';
import { Instructions } from './Instructions';
import { RegenerateActionItems } from './RegenerateActionItems';

const useStyles = makeStyles(() => ({
  modalBox: {
    overflow: 'visible',
  },
}));

export type ImpressionType = KnownImpressionTypes | 'all';

export type ImpressionTypes =
  | {
      state: 'loading';
      data: null;
    }
  | {
      state: 'loaded';
      data: ImpressionType[];
    };

type GenerateMenuModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  patientId: string;
  executiveSummary?: ExecutiveSummaryData;
  isActionItemsRegenerate?: boolean;
};

function GenerateMenuModal({
  isOpen,
  onDismiss,
  patientId,
  executiveSummary,
  isActionItemsRegenerate = false,
}: GenerateMenuModalProps) {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const { isLoading: isLoadingCrds, compositeReportDefinitions } =
    useRecursiveCRDFetch();
  const [crdDurableId, setCrdDurableId] = useState<string>();
  const [instructions, setInstructions] = useState('');
  const [impressionType, setImpressionType] = useState<ImpressionType>('all');

  // reset instructions when crd durable id is set to undefined (using v1 executive summary)
  useEffect(() => {
    if (crdDurableId === undefined) setInstructions('');
  }, [crdDurableId, setInstructions]);

  // set crd durable id to the executive summary's crd durable id if it exists
  useEffect(() => {
    if (!isLoadingCrds && executiveSummary?.compositeReportDefinitionDurableId)
      setCrdDurableId(executiveSummary.compositeReportDefinitionDurableId);
  }, [isLoadingCrds, executiveSummary?.compositeReportDefinitionDurableId]);

  // fetch selected CRD and its templates
  const { data: crd, isLoading: isLoadingCrd } = useTemplatingItemDetailQuery({
    durableId: crdDurableId,
  });
  const { data: templates = [], queries: templateQueries } = useCRDTemplates({
    compositeReportDefinition: crd as
      | CompositeReportDefinitionDetail
      | undefined,
  });
  const isLoadingTemplates = useMemo(
    () => templateQueries.some(q => q.isLoading),
    [templateQueries],
  );
  const isLoadingCrdData =
    isLoadingCrds ||
    (crdDurableId !== undefined && (isLoadingCrd || isLoadingTemplates));

  const impressionTypes = useMemo(() => {
    if (isLoadingCrdData) {
      return { state: 'loading' as const, data: null };
    } else {
      if (crdDurableId === undefined) {
        return {
          state: 'loaded' as const,
          data: [
            'all',
            ...EXECUTIVE_SUMMARY_IMPRESSION_TYPES,
          ] as ImpressionType[],
        };
      } else {
        return {
          state: 'loaded' as const,
          data: [
            'all',
            ...templates.map(template => template.impressionType),
          ] as ImpressionType[],
        };
      }
    }
  }, [isLoadingCrdData, crdDurableId, templates]);

  const isRegenerate =
    executiveSummary !== undefined &&
    executiveSummary.hasContent &&
    executiveSummary.status === 'active';

  const currentImpressionStatus = useMemo(() => {
    if (impressionType === 'all') return 'ai-generated';
    const impression = executiveSummary?.impressions.find(
      impression => impression.impressionType === impressionType,
    );
    return impression === undefined
      ? 'missing'
      : isAiGenerated(impression.impression)
      ? 'ai-generated'
      : 'human-edited';
  }, [executiveSummary?.impressions, impressionType]);

  const { warning: impressionWarning, generateDisabled } = useImpressionWarning(
    {
      currentImpressionStatus,
      isRegenerate,
      executiveSummary,
      isActionItemsRegenerate,
      isCRDSelected: crdDurableId !== undefined,
      selectedImpressionType: impressionType,
      hasInstructions: instructions.trim() !== '',
    },
  );

  const generateAction = isActionItemsRegenerate ? (
    <RegenerateActionItems patientId={patientId} onGenerate={onDismiss} />
  ) : impressionTypes.state === 'loaded' ? (
    <GenerateExecutiveSummary
      patientId={patientId}
      isRegenerate={isRegenerate}
      crdDurableId={crdDurableId}
      instructions={instructions}
      impressionTypeToGenerate={impressionType}
      availableImpressionTypes={impressionTypes.data.filter(
        (i): i is KnownImpressionTypes => i !== 'all',
      )}
      onGenerate={onDismiss}
      disabled={generateDisabled}
    />
  ) : (
    <GenerateExecutiveSummary.Disabled isRegenerate={isRegenerate} />
  );

  const impressionTypeSelect =
    impressionTypes.state === 'loaded' ? (
      <ImpressionTypeSelect
        availableImpressionTypes={impressionTypes.data}
        impressionType={impressionType}
        setImpressionType={setImpressionType}
      />
    ) : (
      <Box direction="column" align="center">
        <DotLoader size={0} />
      </Box>
    );

  return (
    <Modal
      title={formatMessage(messages.generate)}
      isOpen={isOpen}
      onDismiss={onDismiss}
      size={1}
      actions={
        <>
          <Button
            onClick={onDismiss}
            variant="outlined"
            color="negative"
            fullWidth
          >
            {executiveSummary
              ? formatMessage(messages.cancel)
              : formatMessage(messages.exit)}
          </Button>
          {generateAction}
        </>
      }
    >
      <Box direction="column" gap={2} className={classes.modalBox}>
        {!isActionItemsRegenerate && !isLoadingCrds && (
          <CRDSelect
            crdDurableId={crdDurableId}
            setCrdDurableId={setCrdDurableId}
            executiveSummary={executiveSummary}
            compositeReportDefinitions={compositeReportDefinitions}
          />
        )}
        {!isActionItemsRegenerate && isRegenerate && impressionTypeSelect}
        {crdDurableId && (
          <>
            <Divider />
            <Instructions
              instructions={instructions}
              setInstructions={setInstructions}
              isRequired={currentImpressionStatus === 'human-edited'}
            />
          </>
        )}
        {impressionWarning && (
          <Alert statusType="warning">
            <AlertIcon icon={AlertCircle} />
            <AlertBody>
              <Text size="caption" weight="bold">
                {impressionWarning}
              </Text>
            </AlertBody>
          </Alert>
        )}
      </Box>
    </Modal>
  );
}

type UseImpressionWarningArgs = {
  currentImpressionStatus: 'ai-generated' | 'human-edited' | 'missing';
  isRegenerate: boolean;
  executiveSummary: ExecutiveSummaryData | undefined;
  isActionItemsRegenerate: boolean;
  isCRDSelected: boolean;
  selectedImpressionType: ImpressionType;
  hasInstructions: boolean;
};

type UseImpressionWarningResult = {
  warning: string | null;
  generateDisabled: boolean;
};

function useImpressionWarning({
  currentImpressionStatus,
  isRegenerate,
  executiveSummary,
  isActionItemsRegenerate,
  isCRDSelected,
  selectedImpressionType,
  hasInstructions,
}: UseImpressionWarningArgs): UseImpressionWarningResult {
  const { formatMessage } = useIntl();

  // prevent generating a human-edited impression if the impression is not all
  if (
    selectedImpressionType !== 'all' &&
    currentImpressionStatus === 'human-edited'
  ) {
    return {
      warning: formatMessage(messages.impressionV1Error),
      generateDisabled: true,
    };
  }

  // allow generating a new summary if the executive summary is not active
  if (!isRegenerate && !isActionItemsRegenerate && executiveSummary) {
    return {
      warning: formatMessage(messages.generateNewAlert, {
        status: executiveSummary.status,
      }),
      generateDisabled: false,
    };
  }

  // warn user that edited sections will not be regenerated if no CRD is selected or no instructions are provided
  if (
    isRegenerate &&
    (!isCRDSelected || (isCRDSelected && !hasInstructions)) &&
    selectedImpressionType === 'all'
  ) {
    return {
      warning: formatMessage(messages.regenerateAlert),
      generateDisabled: false,
    };
  }

  // warn user that edited sections will be regenerated using provided instructions if CRD is selected and instructions are provided
  if (isRegenerate && isCRDSelected && hasInstructions) {
    return {
      warning: formatMessage(messages.regenerateAllV2WithInstructions),
      generateDisabled: false,
    };
  }

  return {
    warning: null,
    generateDisabled: false,
  };
}

export { GenerateMenuModal };
